import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useContext,
} from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Box,
  Typography,
  TextField,
  Button,
  Chip,
  Slide,
  IconButton,
  Menu,
  MenuItem,
  ListItemText,
  Divider,
  Alert,
  alpha,
  Tooltip,
  Zoom,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import KeyIcon from '@mui/icons-material/Key';
import DeleteIcon from '@mui/icons-material/Delete';
import StoreIcon from '@mui/icons-material/Store';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TipsAndUpdatesOutlinedIcon from '@mui/icons-material/TipsAndUpdatesOutlined';
import { BananaContext } from '@wikimedia/react.i18n';

// Import components
import MessageBubble from './MessageBubble';
import APIKeyConfig from './APIKeyConfig';

// Import Autocomplete for store selector
import FormAutocompleteInput from '@legacy/components/formAutocompleteInput';

// Import hooks
import { useAuth } from '@context/Auth0Context';
import { useUser } from '@hooks/useUser';
import { useSelector } from 'react-redux';

// Import chat service
import ChatService from '@services/ai/chatService';

// Constants
const MAX_CHARS = 300;

// Transition component for dialog animation
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

/**
 * Enhanced AI Chat Component with modern UI
 */
export default function AIChat({ open, onClose }) {
  const chatService = useRef(new ChatService()).current;
  const { accessToken } = useAuth();
  const plMerchants = useSelector((state) => state.user.plMerchants);
  const translator = useContext(BananaContext);

  // Input state
  const [input, setInput] = useState('');
  const [charCount, setCharCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  // Chat state
  const [messages, setMessages] = useState([]);
  const messagesEndRef = useRef(null);

  // API key and dialog state
  const [hasApiKey, setHasApiKey] = useState(false);
  const [apiKeyDialogOpen, setApiKeyDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Store selection state
  const [selectedStore, setSelectedStore] = useState(null);
  const { merchants } = useUser();

  // We no longer use the settings menu

  // Provider menu state
  const [providerMenuAnchorEl, setProviderMenuAnchorEl] = useState(null);
  const providerMenuOpen = Boolean(providerMenuAnchorEl);

  // Confirmation dialog state
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  // Suggestions menu state
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);

  // Get translations using i18n system
  const translations = {
    ai_chat_title: translator.i18n('ai_chat_title'),
    ai_chat_beta: translator.i18n('ai_chat_beta'),
    ai_chat_greeting: translator.i18n('ai_chat_greeting'),
    ai_chat_placeholder: translator.i18n('ai_chat_placeholder'),
    ai_chat_send: translator.i18n('ai_chat_send'),
    ai_chat_close: translator.i18n('ai_chat_close'),
    ai_chat_loading: translator.i18n('ai_chat_loading'),
    ai_chat_error: translator.i18n('ai_chat_error'),
    ai_chat_footer: translator.i18n('ai_chat_footer'),
    ai_chat_store_selection: translator.i18n('ai_chat_store_selection'),
    ai_chat_select_store_warning: translator.i18n(
      'ai_chat_select_store_warning'
    ),
    ai_chat_api_key_required: translator.i18n('ai_chat_api_key_required'),
    ai_chat_api_key_invalid: translator.i18n('ai_chat_api_key_invalid'),
    ai_chat_api_key_missing: translator.i18n('ai_chat_api_key_missing'),
    ai_chat_api_key_configure: translator.i18n('ai_chat_api_key_configure'),
    ai_chat_settings: translator.i18n('ai_chat_settings'),
    ai_chat_configure_api_key: translator.i18n('ai_chat_configure_api_key'),
    ai_chat_clear_chat: translator.i18n('ai_chat_clear_chat'),
    ai_chat_try_asking: translator.i18n('ai_chat_try_asking'),
    ai_chat_suggestions_tooltip: translator.i18n('ai_chat_suggestions_tooltip'),
    ai_chat_confirm_clear: translator.i18n('ai_chat_confirm_clear'),
    ai_chat_confirm_yes: translator.i18n('ai_chat_confirm_yes'),
    ai_chat_confirm_no: translator.i18n('ai_chat_confirm_no'),
  };

  // Suggestions for the AI - using translation keys
  const suggestions = [
    translator.i18n('ai_chat_suggestion_1'),
    translator.i18n('ai_chat_suggestion_2'),
    translator.i18n('ai_chat_suggestion_3'),
    translator.i18n('ai_chat_suggestion_4'),
    translator.i18n('ai_chat_suggestion_5'),
    translator.i18n('ai_chat_suggestion_6'),
  ];

  // Check for API key on load and component open
  useEffect(() => {
    if (open) {
      // Check if API key exists
      checkApiKey();
      
      // Initialize messages from chat history or with greeting
      const savedHistory = chatService.getChatHistory(selectedStore);
      setMessages(
        savedHistory.length > 0
          ? savedHistory
          : [{ isUser: false, text: translations.ai_chat_greeting }]
      );
    }
  }, [open, selectedStore]);

  // Function to check if API key exists
  const checkApiKey = () => {
    const hasKey = chatService.hasApiKey();
    setHasApiKey(hasKey);

    // Show API key dialog if no key is set
    if (!hasKey) {
      setErrorMessage(translations.ai_chat_api_key_missing);
    } else {
      setErrorMessage('');
    }
  };
  
  // Initialize API key check on component mount
  useEffect(() => {
    checkApiKey();
  }, []);

  // Auto-scroll to bottom when messages change
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    if (open) {
      scrollToBottom();
    }
  }, [messages, open, scrollToBottom]);

  // Handle input changes with character limit
  const handleInputChange = useCallback((e) => {
    const inputText = e.target.value;
    if (inputText.length <= MAX_CHARS) {
      setInput(inputText);
      setCharCount(inputText.length);
    }
  }, []);

  // Save messages to local storage when they change
  useEffect(() => {
    if (messages.length > 0) {
      chatService.saveChatHistory(messages, selectedStore);
    }
  }, [messages, selectedStore]);

  // Send message to AI
  const handleSend = useCallback(async () => {
    if (input.trim() === '') return;

    // Check for API key before sending
    if (!hasApiKey) {
      setErrorMessage(translations.ai_chat_api_key_missing);
      setApiKeyDialogOpen(true);
      return;
    }

    const merchantIdsWithAI = merchants
      .filter(
        (merchant) =>
          merchant?.accountConfigurations?.isAIAssistantEnabled === true
      )
      .map((merchant) => merchant.personId);

    const aiEnabledMerchants = plMerchants
      ? plMerchants.filter((merchant) =>
          merchantIdsWithAI.includes(Number(merchant.value))
        )
      : [];
    if (!selectedStore && aiEnabledMerchants.length > 0) {
      const updatedMessages = [
        ...messages,
        { isUser: true, text: input },
        { isUser: false, text: translations.ai_chat_select_store_warning },
      ];
      setMessages(updatedMessages);
      chatService.saveChatHistory(updatedMessages, selectedStore);
      setInput('');
      setCharCount(0);
      return;
    }

    // Add user message to chat
    const userMessage = { isUser: true, text: input };
    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setInput('');
    setCharCount(0);
    setIsLoading(true);
    setSuggestionsOpen(false);

    try {
      // Send message to AI
      const response = await chatService.sendMessage(
        input,
        messages,
        accessToken,
        selectedStore
      );

      // Add AI response to chat
      const finalMessages = [
        ...updatedMessages,
        { isUser: false, text: response },
      ];

      setMessages(finalMessages);
      chatService.saveChatHistory(finalMessages, selectedStore);
    } catch (error) {
      console.error(`Error sending message:`, error);

      // Handle error cases
      if (error.message.includes('API key')) {
        setErrorMessage(translations.ai_chat_api_key_invalid);
        setApiKeyDialogOpen(true);
      } else if (error.message.includes('Auth token')) {
        // Handle auth token issues
        const errorMessages = [
          ...updatedMessages,
          {
            isUser: false,
            text: `${translations.ai_chat_error} Authentication issue. Please try logging in again.`,
          },
        ];
        setMessages(errorMessages);
        chatService.saveChatHistory(errorMessages, selectedStore);
      } else {
        const errorMessages = [
          ...updatedMessages,
          { isUser: false, text: translations.ai_chat_error },
        ];
        setMessages(errorMessages);
        chatService.saveChatHistory(errorMessages, selectedStore);
      }
    } finally {
      setIsLoading(false);
    }
  }, [input, hasApiKey, messages, accessToken, selectedStore, plMerchants]);

  // Handle keyboard shortcuts
  const handleKeyPress = useCallback(
    (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  // Character counter color based on limit
  const getCounterColor = useCallback((count) => {
    if (count > MAX_CHARS * 0.8) {
      return count === MAX_CHARS ? 'error.main' : 'warning.main';
    }
    return 'text.secondary';
  }, []);

  // We've moved the settings functionality to direct buttons in the header

  // API key dialog handlers
  const handleOpenApiKeyDialog = () => {
    setApiKeyDialogOpen(true);
  };

  const handleCloseApiKeyDialog = () => {
    setApiKeyDialogOpen(false);
  };

  // Handle API key saved
  const handleApiKeySaved = () => {
    checkApiKey();
  };

  // Show clear confirmation dialog
  const handleShowClearConfirmation = () => {
    setConfirmDialogOpen(true);
  };

  // Handle confirmation dialog close
  const handleConfirmationClose = () => {
    setConfirmDialogOpen(false);
  };

  // Clear chat history after confirmation
  const handleClearChat = () => {
    const initialMessage = {
      isUser: false,
      text: translations.ai_chat_greeting,
    };
    setMessages([initialMessage]);

    chatService.clearChatHistory(selectedStore);
    chatService.saveChatHistory([initialMessage], selectedStore);

    // Close the confirmation dialog
    setConfirmDialogOpen(false);
  };

  // Provider menu handlers
  const handleProviderMenuClose = () => {
    setProviderMenuAnchorEl(null);
  };

  const handleProviderChange = (provider) => {
    if (provider !== chatService.getProvider()) {
      // Set the new provider
      chatService.setProvider(provider);

      // Load history for the new provider or set initial message
      const savedHistory = chatService.getChatHistory(selectedStore);
      if (savedHistory.length > 0) {
        setMessages(savedHistory);
      } else {
        const initialMessage = {
          isUser: false,
          text: translations.ai_chat_greeting,
        };
        setMessages([initialMessage]);
        chatService.saveChatHistory([initialMessage], selectedStore);
      }

      // Check for API key
      checkApiKey();
    }

    handleProviderMenuClose();
  };

  // Handle store change
  const handleStoreChange = (event, value) => {
    setSelectedStore(value);

    // Load chat history for the selected store
    if (value !== selectedStore) {
      const storeHistory = chatService.getChatHistory(value);
      setMessages(
        storeHistory.length > 0
          ? storeHistory
          : [{ isUser: false, text: translations.ai_chat_greeting }]
      );
    }
  };

  // Handle suggestion click
  const handleSuggestionClick = (suggestion) => {
    setInput(suggestion);
    setCharCount(suggestion.length);
    setSuggestionsOpen(false);

    // Auto-focus the input field after selecting a suggestion
    document.getElementById('chat-input-field')?.focus();
  };

  // Toggle suggestions panel
  const toggleSuggestions = () => {
    setSuggestionsOpen((prev) => !prev);
  };

  // Detect mobile screens
  const isMobile = window.matchMedia('(max-width: 600px)').matches;

  return (
    <>
      <Dialog
        fullWidth
        fullScreen={isMobile}
        maxWidth="md"
        open={open}
        onClose={onClose}
        TransitionComponent={Transition}
        PaperProps={{
          sx: {
            height: { xs: '100%', sm: '80vh' },
            display: 'flex',
            flexDirection: 'column',
            borderRadius: { xs: 0, sm: '12px' },
            overflow: 'hidden',
            maxWidth: { xs: '100%', md: '850px' },
            m: { xs: 0, sm: 2 },
            border: 0,
            borderWidth: 0,
            borderStyle: 'none',
            padding: 0,
            boxShadow: (theme) => {
              if (theme.breakpoints.down('sm')) return 'none';
              return theme.palette.mode === 'dark'
                ? '0 8px 24px rgba(0, 0, 0, 0.4)'
                : '0 8px 32px rgba(0, 0, 0, 0.1)';
            },
          },
        }}
      >
        {/* Dialog Header */}
        <DialogTitle
          sx={{
            py: { xs: 1.5, sm: 2 },
            px: { xs: 2, sm: 3 },
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: (theme) =>
              theme.palette.mode === 'dark' ? 'background.paper' : 'white',
            borderBottom: 'none',
            boxShadow: { xs: '0 1px 3px rgba(0,0,0,0.05)', sm: 'none' },
          }}
        >
          {/* Title and Actions */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mr: 1 }}>
                {translations.ai_chat_title}
              </Typography>
              <Chip
                label={translations.ai_chat_beta}
                color="warning"
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.625rem',
                  fontWeight: 'bold',
                  '& .MuiChip-label': { px: 1 },
                }}
              />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Chip
                label={
                  chatService.getProvider() === 'openai' ? 'ChatGPT' : 'Claude'
                }
                size="small"
                color="default"
                variant="outlined"
                onClick={(event) =>
                  setProviderMenuAnchorEl(event.currentTarget)
                }
                sx={{
                  mr: 1,
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: (theme) =>
                      alpha(theme.palette.primary.main, 0.1),
                  },
                }}
              />
              <Tooltip
                title={translations.ai_chat_configure_api_key}
                TransitionComponent={Zoom}
                arrow
              >
                <IconButton
                  aria-label="Configure API Key"
                  onClick={handleOpenApiKeyDialog}
                  size="small"
                  sx={{ mr: 1 }}
                >
                  <KeyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <IconButton
                edge="end"
                color="inherit"
                onClick={onClose}
                aria-label="Close AI Chat dialog"
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>

          {/* Store selector */}
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
            <Box sx={{ width: '100%' }}>
              {(() => {
                const merchantIdsWithAI = merchants
                  .filter(
                    (merchant) =>
                      merchant?.accountConfigurations?.isAIAssistantEnabled ===
                      true
                  )
                  .map((merchant) => merchant.personId);
                const aiEnabledMerchants = plMerchants
                  ? plMerchants.filter((merchant) =>
                      merchantIdsWithAI.includes(Number(merchant.value))
                    )
                  : [];

                return (
                  <FormAutocompleteInput
                    options={aiEnabledMerchants}
                    value={selectedStore}
                    placeholder="test"
                    onChange={handleStoreChange}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <StoreIcon sx={{ mr: 1, fontSize: '1rem' }} />
                        <Typography variant="caption">
                          {translations.ai_chat_store_selection}
                        </Typography>
                      </Box>
                    }
                  />
                );
              })()}
            </Box>
          </Box>
        </DialogTitle>

        {/* Add divider between header and content - only in light theme */}
        <Divider
          sx={{
            borderColor: (theme) =>
              theme.palette.mode === 'dark'
                ? alpha(theme.palette.divider, 0.3)
                : 'rgba(0, 0, 0, 0.08)',
            display: (theme) =>
              theme.palette.mode === 'dark'
                ? 'none'
                : { xs: 'none', sm: 'block' },
          }}
        />

        {/* Provider Selection Menu */}
        <Menu
          anchorEl={providerMenuAnchorEl}
          open={providerMenuOpen}
          onClose={handleProviderMenuClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          transformOrigin={{ vertical: 'top', horizontal: 'center' }}
          PaperProps={{
            sx: {
              mt: 1,
              minWidth: 150,
              boxShadow: '0 2px 10px rgba(0,0,0,0.15)',
            },
          }}
        >
          <MenuItem
            onClick={() => handleProviderChange('openai')}
            selected={chatService.getProvider() === 'openai'}
          >
            <ListItemText>ChatGPT</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={() => handleProviderChange('anthropic')}
            selected={chatService.getProvider() === 'anthropic'}
          >
            <ListItemText>Claude</ListItemText>
          </MenuItem>
        </Menu>

        {/* Chat Content */}
        <DialogContent
          sx={{
            p: { xs: 2, sm: 3 },
            pt: { xs: 3, sm: 4 },
            flexGrow: 1,
            overflowY: 'auto',
            backgroundColor: (theme) =>
              theme.palette.mode === 'dark'
                ? alpha(theme.palette.background.default, 0.7)
                : '#f8f9fa',
            border: 'none',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Error message alert */}
          {errorMessage && (
            <Alert
              severity="warning"
              sx={{
                mb: 2,
                '& .MuiAlert-icon': { alignItems: 'center' },
                '& .MuiAlert-message': {
                  display: 'flex',
                  alignItems: 'center',
                },
              }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={handleOpenApiKeyDialog}
                >
                  {translations.ai_chat_api_key_configure}
                </Button>
              }
            >
              {errorMessage}
            </Alert>
          )}

          {/* Auth status alert */}
          {hasApiKey && !accessToken && (
            <Alert
              severity="info"
              sx={{ mb: 2, '& .MuiAlert-icon': { alignItems: 'center' } }}
            >
              Authentication token not available. Some capabilities may be
              limited.
            </Alert>
          )}

          {/* Messages area */}
          <Box sx={{ flexGrow: 1 }}>
            {messages.map((message, index) => (
              <MessageBubble
                key={index}
                isUser={message.isUser}
                text={message.text}
                translations={translations}
                storeId={selectedStore?.value || null}
              />
            ))}
            {isLoading && (
              <MessageBubble
                isUser={false}
                isLoading={true}
                text=""
                translations={translations}
                storeId={selectedStore?.value || null}
              />
            )}
          </Box>

          {/* Empty space for scroll reference */}
          <div ref={messagesEndRef} />
        </DialogContent>

        {/* Bottom divider - only in light theme */}
        <Divider
          sx={{
            borderColor: (theme) =>
              theme.palette.mode === 'dark'
                ? alpha(theme.palette.divider, 0.3)
                : 'rgba(0, 0, 0, 0.08)',
            display: (theme) =>
              theme.palette.mode === 'dark' ? 'none' : 'block',
          }}
        />

        {/* Input area */}
        <Box
          sx={{
            p: { xs: 1.5, sm: 2 },
            pt: { xs: 1, sm: 2 },
            backgroundColor: (theme) =>
              theme.palette.mode === 'dark' ? 'background.paper' : 'white',
            borderTop: (theme) =>
              theme.palette.mode === 'dark'
                ? 'none'
                : '1px solid rgba(0, 0, 0, 0.08)',
            pb: {
              xs: isMobile ? 'env(safe-area-inset-bottom, 16px)' : 1.5,
              sm: 2,
            },
            position: 'relative',
          }}
        >
          {suggestionsOpen && (
            <Box
              sx={{
                position: 'absolute',
                bottom: '100%',
                left: 0,
                right: 0,
                p: 2,
                mx: { xs: 1.5, sm: 2 },
                borderRadius: '8px',
                backgroundColor: (theme) =>
                  theme.palette.mode === 'dark'
                    ? theme.palette.background.paper
                    : '#ffffff',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                border: '1px solid',
                borderColor: (theme) =>
                  theme.palette.mode === 'dark'
                    ? alpha(theme.palette.divider, 0.3)
                    : alpha(theme.palette.primary.main, 0.1),
                zIndex: 10,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                <TipsAndUpdatesOutlinedIcon
                  fontSize="small"
                  sx={{
                    mr: 1,
                    color: (theme) =>
                      theme.palette.mode === 'dark'
                        ? theme.palette.primary.light
                        : theme.palette.primary.main,
                  }}
                />
                <Typography variant="subtitle2">
                  {translations.ai_chat_try_asking}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {suggestions.map((suggestion, index) => (
                  <Chip
                    key={index}
                    label={suggestion}
                    onClick={() => handleSuggestionClick(suggestion)}
                    sx={{
                      cursor: 'pointer',
                      backgroundColor: (theme) =>
                        theme.palette.mode === 'dark'
                          ? alpha(theme.palette.primary.dark, 0.2)
                          : alpha(theme.palette.primary.light, 0.1),
                      color: 'text.primary',
                      '&:hover': {
                        backgroundColor: (theme) =>
                          theme.palette.mode === 'dark'
                            ? alpha(theme.palette.primary.dark, 0.4)
                            : alpha(theme.palette.primary.light, 0.25),
                      },
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}

          {/* Input container with clear chat button */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              borderRadius: { xs: 8, sm: 2 },
              boxShadow: (theme) => {
                if (theme.palette.mode === 'dark') return 0;
                return isMobile
                  ? '0 1px 2px rgba(0,0,0,0.12)'
                  : '0 2px 4px rgba(0,0,0,0.08)';
              },
              p: { xs: 0.75, sm: 1 },
              backgroundColor: (theme) =>
                theme.palette.mode === 'dark'
                  ? alpha(theme.palette.background.paper, 0.7)
                  : 'white',
              border: '1px solid',
              borderColor: (theme) =>
                theme.palette.mode === 'dark'
                  ? alpha(theme.palette.divider, 0.3)
                  : 'rgba(0,0,0,0.1)',
              position: 'relative',
              zIndex: 1,
            }}
          >
            {/* Ideas/suggestions button */}
            <Tooltip
              title={translator.i18n('ai_chat_suggestions_tooltip')}
              arrow
            >
              <IconButton
                size="small"
                onClick={toggleSuggestions}
                sx={{
                  mr: 1,
                  color: suggestionsOpen ? 'primary.main' : 'action.active',
                }}
              >
                <TipsAndUpdatesIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            {/* Clear chat button */}
            <Tooltip title={translations.ai_chat_clear_chat} arrow>
              <IconButton
                size="small"
                onClick={handleShowClearConfirmation}
                sx={{ mr: 1, color: 'error.main' }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            {/* Text input field */}
            <Box sx={{ width: '100%', position: 'relative' }}>
              <TextField
                id="chat-input-field"
                fullWidth
                multiline
                maxRows={isMobile ? 3 : 4}
                placeholder={translations.ai_chat_placeholder}
                value={input}
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
                variant="standard"
                InputProps={{ disableUnderline: true }}
                sx={{
                  '& .MuiInputBase-root': {
                    p: { xs: 0.75, sm: 1 },
                    fontSize: '0.875rem',
                  },
                  '& .MuiInputBase-inputMultiline': {
                    lineHeight: { xs: 1.3, sm: 1.5 },
                  },
                }}
              />
              <Typography
                variant="caption"
                color={getCounterColor(charCount)}
                sx={{
                  position: 'absolute',
                  bottom: -6,
                  right: 8,
                  fontSize: '0.7rem',
                  opacity: 0.8,
                }}
              >
                {charCount}/{MAX_CHARS}
              </Typography>
            </Box>

            {/* Send button */}
            <Button
              variant="contained"
              color="primary"
              disableElevation
              sx={{
                ml: 1,
                px: 2,
                borderRadius: '50%',
                minWidth: { xs: 36, sm: 40 },
                width: { xs: 36, sm: 40 },
                height: { xs: 36, sm: 40 },
                padding: 0,
              }}
              onClick={handleSend}
              disabled={input.trim() === ''}
              aria-label={translations.ai_chat_send}
            >
              <SendIcon fontSize={isMobile ? 'small' : 'small'} />
            </Button>
          </Box>

          {/* Footer text */}
          <Typography
            variant="caption"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mt: 1.5,
              textAlign: 'center',
              fontSize: '0.7rem',
              color: (theme) =>
                theme.palette.mode === 'dark'
                  ? 'text.secondary'
                  : 'rgba(0, 0, 0, 0.6)',
              fontWeight: 500,
              letterSpacing: '0.01em',
            }}
          >
            <InfoOutlinedIcon
              fontSize="inherit"
              sx={{ mr: 0.5, fontSize: '0.8rem' }}
            />
            {translations.ai_chat_footer}
          </Typography>
        </Box>
      </Dialog>

      {/* API Key Configuration Dialog */}
      <APIKeyConfig
        open={apiKeyDialogOpen}
        onClose={handleCloseApiKeyDialog}
        onSave={handleApiKeySaved}
        chatService={chatService}
      />

      {/* Clear Chat Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleConfirmationClose}
        aria-labelledby="clear-chat-confirmation-dialog"
        PaperProps={{ sx: { borderRadius: '12px', maxWidth: '400px', p: 0.5 } }}
      >
        <DialogTitle id="clear-chat-confirmation-dialog">
          {translations.ai_chat_clear_chat}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {translations.ai_chat_confirm_clear}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ pb: 2, px: 3 }}>
          <Button
            onClick={handleConfirmationClose}
            color="primary"
            variant="outlined"
          >
            {translations.ai_chat_confirm_no}
          </Button>
          <Button
            onClick={handleClearChat}
            color="error"
            variant="contained"
            autoFocus
          >
            {translations.ai_chat_confirm_yes}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
